import { createElement, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { addressText, validateDetails } from "./checkoutModel";
import { checkoutError, createOrderSubmission, loadCheckoutIdentity, prepareCheckout } from "./checkoutService";
import "./checkout.css";

const money = value => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(value);

export function CustomerCheckout({ user, cart, onLogin, onSuccess }) {
  const [details, setDetails] = useState({ name: "", address: "", latitude: "", longitude: "" });
  const [addresses, setAddresses] = useState([]);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [locating, setLocating] = useState(false);
  const [success, setSuccess] = useState(null);
  const [attempted, setAttempted] = useState(false);
  const guard = useRef(false), submission = useRef(null), mounted = useRef(true);
  const submittedCart = useRef(null), currentCart = useRef(cart);
  currentCart.current = cart;
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  useEffect(() => {
    if (!user || success || attempted) return undefined;
    let cancelled = false;
    setQuote(null);
    prepareCheckout(user.uid, cart).then(value => { if (!cancelled) setQuote(value); }).catch(e => { if (!cancelled) setError(checkoutError(e)); });
    return () => { cancelled = true; };
  }, [user, cart, success, attempted]);
  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    loadCheckoutIdentity(user.uid).then(value => {
      if (cancelled) return;
      setAddresses(value.addresses);
      setDetails(d => ({ ...d, name: d.name || value.name }));
    }).catch(() => { if (!cancelled) setError("Saved details could not be loaded. You can enter your name and address below."); });
    return () => { cancelled = true; };
  }, [user]);
  const update = (field, value) => setDetails(d => ({ ...d, [field]: value }));
  const locate = () => {
    setError("");
    if (!navigator.geolocation) { setError("Location is unavailable in this browser. Enter coordinates below."); return; }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(position => {
      if (!mounted.current) return;
      setDetails(d => ({ ...d, latitude: String(position.coords.latitude), longitude: String(position.coords.longitude) }));
      setLocating(false);
    }, () => {
      if (!mounted.current) return;
      setError("Could not get your location. Allow location access and retry, or enter coordinates below."); setLocating(false);
    }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 });
  };
  const place = async event => {
    event.preventDefault();
    if (guard.current || success) return;
    guard.current = true; setBusy(true); setError("");
    try {
      validateDetails(details, user);
      if (!submission.current) {
        const fresh = await prepareCheckout(user.uid, cart, { latitude: Number(details.latitude), longitude: Number(details.longitude) });
        if (!mounted.current) return;
        if (JSON.stringify(currentCart.current) !== JSON.stringify(cart)) throw new Error("Your cart changed. Review checkout again.");
        if (JSON.stringify(fresh) !== JSON.stringify(quote)) {
          setQuote(fresh); throw new Error("Prices or delivery charges changed. Review the updated summary and press Place Order again.");
        }
        submission.current = createOrderSubmission(user.uid);
        submittedCart.current = cart.map(item => ({ ...item }));
        setAttempted(true);
      }
      const result = await submission.current(details, quote);
      if (mounted.current) setSuccess(result);
      // Successful order state is established before local cart cleanup.
      try { onSuccess(submittedCart.current); } catch (_) { /* Never report a confirmed order as failed. */ }
    } catch (e) { if (mounted.current) setError(checkoutError(e)); }
    finally { guard.current = false; if (mounted.current) setBusy(false); }
  };
  if (success) return <main className="qd-main qd-checkout"><h1>Order placed</h1><p role="status">Your order {success.orderId} is Pending. Pay cash on delivery.</p><Link to="/">Continue shopping</Link></main>;
  if (!user) return <main className="qd-main qd-checkout"><h1>Checkout</h1><p>Log in to place an order with your customer account.</p><button type="button" onClick={onLogin}>Login with mobile</button><Link to="/cart">Back to cart</Link></main>;
  if (!cart.length) return <main className="qd-main qd-checkout"><h1>Your cart is empty</h1><Link to="/">Continue shopping</Link></main>;
  return <main className="qd-main qd-checkout"><Link to="/cart">Back to cart</Link><h1>Checkout</h1><form onSubmit={place}>
    <section><h2>Delivery details</h2><fieldset disabled={busy || attempted}>
      <label>Full name<input value={details.name} onChange={e => update("name", e.target.value)} autoComplete="name" required minLength={2} maxLength={120} /></label>
      <label>Mobile number<input value={user.phoneNumber || ""} readOnly autoComplete="tel" /></label>
      {addresses.length > 0 && <label>Saved address<select defaultValue="" onChange={e => {
        const selected = addresses.find(a => a.id === e.target.value);
        if (selected) setDetails(d => ({ ...d, address: addressText(selected), latitude: "", longitude: "" }));
      }}><option value="" disabled>Select a saved address</option>{addresses.map(a => createElement("option", { key: a.id, value: a.id }, (a.label || "Address") + ": " + addressText(a)))}</select></label>}
      <label>Delivery address<textarea value={details.address} onChange={e => update("address", e.target.value)} autoComplete="street-address" required minLength={10} maxLength={1000} /></label>
      <h2>Delivery location</h2><button type="button" disabled={locating} onClick={locate}>{locating ? "Finding location…" : "Use my current location"}</button>
      <p>Your address text stays unchanged. Confirm these coordinates belong to your delivery address.</p>
      <div className="qd-checkout-coordinates"><label>Latitude<input type="number" step="any" min="-90" max="90" required value={details.latitude} onChange={e => update("latitude", e.target.value)} /></label><label>Longitude<input type="number" step="any" min="-180" max="180" required value={details.longitude} onChange={e => update("longitude", e.target.value)} /></label></div>
      <small>You can enter coordinates from your preferred map if GPS is unavailable.</small>
    </fieldset></section>
    <aside><h2>Order summary</h2>{quote ? <><ul>{quote.items.map(item => <li key={item.productId}><span>{item.name} × {item.quantity}</span><b>{money(item.itemTotal)}</b></li>)}</ul><p>Subtotal <b>{money(quote.subtotal)}</b></p><p>Delivery <b>{money(quote.deliveryCharge)}</b></p><p>Total <b>{money(quote.subtotal + quote.deliveryCharge)}</b></p><p>Payment: Cash on Delivery</p></> : <p>Checking prices and delivery availability…</p>}
      {error && <div role="alert" className="qd-login-error">{error}</div>}
      {attempted && !success && <p>Keep this checkout open until the order is confirmed. Retry here if confirmation fails.</p>}
      <button type="submit" disabled={!quote || busy || locating}>{busy ? "Confirming order…" : attempted ? "Check / retry this order" : "Place Order"}</button>
    </aside>
  </form></main>;
}
